import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { productService } from '@/services/product.service';
import { Product } from '@/types';
import { ProductCard } from '@/components/product/ProductCard';
import { ProductCardSkeleton } from '@/components/common/Skeleton';
import { SearchBar } from '@/components/common/SearchBar';

export const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<{ id: string; name: string; slug: string }[]>([]);

  useEffect(() => {
    if (!q) return;
    setLoading(true);
    productService.search(q).then(res => {
      setProducts(res.data.data.products || []);
      setCategories(res.data.data.categories || []);
    }).finally(() => setLoading(false));
  }, [q]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="max-w-2xl mb-8">
        <SearchBar />
      </div>

      {q && (
        <h1 className="font-heading font-bold text-xl text-gray-800 mb-6">
          {loading ? 'Searching...' : `${products.length} results for "${q}"`}
        </h1>
      )}

      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          <span className="text-sm text-gray-500 font-medium">Categories:</span>
          {categories.map(c => (
            <Link key={c.id} to={`/categories/${c.slug}`} className="text-sm bg-primary-50 text-primary-700 px-3 py-1 rounded-full hover:bg-primary-100 transition-colors">
              {c.name}
            </Link>
          ))}
        </div>
      )}

      {!q && (
        <div className="text-center py-16">
          <Search className="h-16 w-16 text-gray-200 mx-auto mb-4" />
          <p className="text-gray-500">Type something to search for medicines, vitamins, skincare...</p>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {loading ? Array(10).fill(0).map((_, i) => <ProductCardSkeleton key={i} />) : products.map(p => <ProductCard key={p.id} product={p} />)}
      </div>

      {!loading && q && products.length === 0 && (
        <div className="text-center py-16">
          <Search className="h-16 w-16 text-gray-200 mx-auto mb-4" />
          <h2 className="font-heading font-semibold text-xl text-gray-700 mb-2">No results found</h2>
          <p className="text-gray-500">Try different keywords or browse our categories.</p>
          <div className="flex flex-wrap justify-center gap-2 mt-6">
            {['Medicines', 'Vitamins', 'Skincare', 'Ayurvedic'].map(c => (
              <Link key={c} to={`/categories/${c.toLowerCase()}`} className="px-4 py-2 bg-primary-50 text-primary-700 rounded-xl text-sm font-medium hover:bg-primary-100">
                {c}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
